import { useEffect, useRef, useState } from 'react';
import {
  ArchiveBoxIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  EllipsisVerticalIcon,
  MapPinIcon,
  MinusCircleIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import type { SignupSheet } from '@flock-types/signupSheets.types';

interface Props {
  sheet: SignupSheet;
  editMode: boolean;
  onPin: () => void;
  onEditDetails: () => void;
  onToggleEditMode: () => void;
  onArchive: () => void;
  onDelete: () => void;
}

/** Leader ⋮ menu (port of the Expo header menu modal). */
export default function SheetMenu({
  sheet,
  editMode,
  onPin,
  onEditDetails,
  onToggleEditMode,
  onArchive,
  onDelete,
}: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const item = (
    label: string,
    Icon: typeof MapPinIcon,
    onClick: () => void,
    className = 'text-flock-navy-800'
  ) => (
    <button
      onClick={() => {
        setOpen(false);
        onClick();
      }}
      className={`flex w-full items-center gap-3 border-b border-flock-gray-100 px-4 py-2.5 text-left text-sm transition last:border-b-0 hover:bg-flock-navy-50 ${className}`}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {label}
    </button>
  );

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        title="Sheet options"
        className="rounded-lg p-2 text-flock-navy-500 transition hover:bg-flock-navy-50 hover:text-flock-navy-700"
      >
        <EllipsisVerticalIcon className="h-5 w-5" />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-40 mt-1 min-w-[200px] overflow-hidden rounded-xl border border-flock-gray-200 bg-white shadow-lg">
          {item(sheet.is_pinned ? 'Unpin' : 'Pin to Top', MapPinIcon, onPin)}
          {item('Edit Details', PencilSquareIcon, onEditDetails)}
          {item(
            editMode ? 'Done Editing' : 'Edit Items',
            editMode ? CheckCircleIcon : MinusCircleIcon,
            onToggleEditMode,
            editMode ? 'text-green-600' : 'text-flock-navy-800'
          )}
          {item(
            sheet.status === 'archived' ? 'Unarchive' : 'Archive',
            sheet.status === 'archived' ? ArrowUturnLeftIcon : ArchiveBoxIcon,
            onArchive,
            sheet.status === 'archived' ? 'text-flock-navy-800' : 'text-amber-600'
          )}
          {item('Delete', TrashIcon, onDelete, 'text-red-500')}
        </div>
      )}
    </div>
  );
}
